/**
 * LabelPrintDialog - Shipping label print fallback dialog
 *
 * Shown when the print agent auto-print fails. Provides:
 * - "Send to Printer" to retry the print agent
 * - "Print" to use browser print (via hidden iframe)
 * - "Download" to save the label file
 * - "Done" to confirm printing and close
 *
 * WAREHOUSE UX:
 * - h-14 buttons for gloved hands
 * - Clear status indicator for print agent
 * - Large tracking number display
 * - Minimal actions — no clutter
 */

import { useCallback, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { printHtmlViaIframe, downloadBlob } from '@/lib/print-utils';
import { stripOrderNamePrefix } from '@threadheads/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Printer,
  Check,
  Download,
  AlertTriangle,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePrintAgent } from '@/features/print-station/hooks/usePrintAgent';

/** Decode base64 to a blob URL. */
function createBlobUrl(base64: string, mimeType: string): string {
  const byteCharacters = atob(base64);
  const byteNumbers = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([byteNumbers], { type: mimeType }));
}

interface LabelPrintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labelBase64: string | null;
  labelType?: string | null;
  /** Nullable: Portless cross-dock labels carry no tracking at pull time. */
  trackingNumber: string | null;
  carrier: string;
  orderName: string;
  onPrintComplete: () => void;
  isReprint?: boolean;
}

export function LabelPrintDialog({
  open,
  onOpenChange,
  labelBase64,
  labelType,
  trackingNumber,
  carrier,
  orderName,
  onPrintComplete,
  isReprint = false,
}: LabelPrintDialogProps) {
  const { t } = useTranslation('production');
  const doneButtonRef = useRef<HTMLButtonElement>(null);
  const [isSending, setIsSending] = useState(false);
  const [agentResult, setAgentResult] = useState<'idle' | 'success' | 'failed'>('idle');
  const { printLabel: agentPrintLabel, labelPrinter, isConnected } = usePrintAgent();

  // Normalize format: StarShipIt sometimes returns carrier names instead of file formats
  const VALID_FORMATS = ['PDF', 'ZPL', 'PNG', 'JPG', 'JPEG'];
  const rawFormat = (labelType ?? 'PDF').toUpperCase();
  const format = VALID_FORMATS.includes(rawFormat) ? rawFormat : 'PDF';
  const isPdf = format === 'PDF';

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setAgentResult('idle');
      setIsSending(false);
    }
  }, [open]);

  // Focus done button when dialog opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => doneButtonRef.current?.focus(), 150);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    onPrintComplete();
  }, [onOpenChange, onPrintComplete]);

  // Send to print agent
  const handleSendToPrinter = useCallback(async () => {
    if (!labelBase64) return;
    setIsSending(true);
    setAgentResult('idle');
    try {
      const result = await agentPrintLabel({
        data: labelBase64,
        format: format,
      });
      if (result.success) {
        setAgentResult('success');
        toast.success(t('packer.labelPrint.labelSent'));
      } else {
        setAgentResult('failed');
        toast.error(t('packer.labelPrint.couldNotReach'), {
          description: t('packer.labelPrint.useBrowserPrint'),
        });
      }
    } catch {
      setAgentResult('failed');
      toast.error(t('packer.labelPrint.couldNotReach'));
    } finally {
      setIsSending(false);
    }
  }, [labelBase64, format, agentPrintLabel, t]);

  // Browser print via hidden iframe
  const handleBrowserPrint = useCallback(() => {
    if (!labelBase64) return;
    try {
      if (isPdf) {
        const url = createBlobUrl(labelBase64, 'application/pdf');
        const win = window.open(url, '_blank');
        if (win) {
          toast.success(t('packer.labelPrint.labelOpened'));
        } else {
          toast.error(t('packer.labelPrint.popupBlocked'));
        }
        // Clean up blob after a delay
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } else {
        const dataUrl = `data:image/png;base64,${labelBase64}`;
        const html = `<!DOCTYPE html>
<html><head><title>Label - ${orderName}</title>
<style>@page{margin:0}body{margin:0;display:flex;justify-content:center}img{max-width:100%;height:auto}</style>
</head><body><img src="${dataUrl}" /></body></html>`;
        printHtmlViaIframe(html);
      }
    } catch {
      toast.error(t('packer.labelPrint.failedToPrint'));
    }
  }, [labelBase64, isPdf, orderName, t]);

  // Download label file
  const handleDownload = useCallback(() => {
    if (!labelBase64) return;
    try {
      const ext = isPdf ? 'pdf' : 'png';
      const mime = isPdf ? 'application/pdf' : 'image/png';
      const url = createBlobUrl(labelBase64, mime);
      downloadBlob(url, `label-${stripOrderNamePrefix(orderName)}.${ext}`);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('packer.labelPrint.downloadFailed'));
    }
  }, [labelBase64, isPdf, orderName, t]);

  const printerName = labelPrinter || 'Label Printer';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg">{t('packer.labelPrint.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('packer.labelPrint.description', { orderName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Reprint warning */}
          {isReprint && (
            <div className="flex items-center gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t('packer.labelPrint.alreadyPrinted')}
            </div>
          )}

          {/* Tracking info card */}
          <div className="rounded-lg border bg-muted/50 p-4 text-center space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('packer.labelPrint.tracking')}</p>
            <p className="text-lg font-mono font-bold tracking-wide break-all">
              {trackingNumber ?? '-'}
            </p>
            <p className="text-sm text-muted-foreground">{carrier}</p>
          </div>

          {/* Print agent status + send button */}
          <div className="space-y-2">
            <Button
              onClick={handleSendToPrinter}
              disabled={!labelBase64 || isSending || agentResult === 'success'}
              className="w-full h-14 text-base font-semibold gap-2.5"
              size="lg"
            >
              {isSending ? (
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : agentResult === 'success' ? (
                <Check className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Printer className="h-5 w-5" aria-hidden="true" />
              )}
              {isSending
                ? t('packer.labelPrint.sending')
                : agentResult === 'success'
                  ? t('packer.labelPrint.sentToPrinter')
                  : t('packer.labelPrint.sendTo', { printer: printerName })}
            </Button>

            {/* Agent connection hint */}
            <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${
                  isConnected ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
              {isConnected ? t('packer.labelPrint.connectedTo', { printer: printerName }) : t('packer.labelPrint.agentNotDetected')}
            </div>
          </div>

          {/* Fallback actions */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={handleBrowserPrint}
              disabled={!labelBase64}
              variant="outline"
              className="h-11 text-sm font-medium gap-2"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              {isPdf ? t('packer.labelPrint.openPdf') : t('packer.labelPrint.browserPrint')}
            </Button>
            <Button
              onClick={handleDownload}
              disabled={!labelBase64}
              variant="outline"
              className="h-11 text-sm font-medium gap-2"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              {t('common:download')}
            </Button>
          </div>

          {/* Done button */}
          <Button
            ref={doneButtonRef}
            onClick={handleClose}
            variant="secondary"
            className="w-full h-14 text-base font-semibold gap-2.5"
            size="lg"
          >
            <Check className="h-5 w-5" aria-hidden="true" />
            {t('common:done')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
